import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Chip,
  OutlinedInput,
} from "@mui/material";
import Footer from "./footer";
import { COLORS, fontFamilyAll } from "../assets/commonComponents/COLORS";
import { mediaData } from "../assets/data/Data";
import ToastMessages from "../assets/commonComponents/ToastMessages";
import { submitFormAPI } from "../Util/UtilApi";

export default function ContactUs({ data, formData }) {
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const titleOptions = ["Mr.", "Ms.", "Mrs.", "Dr.", "Prof."];

  // Communication method options (UI labels)
  const communicationOptions = ["Email", "Phone", "SMS", "WhatsApp"];

  // Helper: Convert array to "Email;Call;SMS;Whatsapp" format
  const joinCommunicationMethods = (methods) => {
    if (!methods || methods.length === 0) return null;

    const mapping = {
      Email: "Email",
      Phone: "Call",
      SMS: "SMS",
      WhatsApp: "Whatsapp",
      // "In-person": "In-person",
    };

    return methods.map((m) => mapping[m] || m).join(";");
  };

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleMultiSelectChange = (event) => {
    const {
      target: { value },
    } = event;
    handleChange("Preferred Communication Method", value);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    let tempErrors = {};
    let isValid = true;

    // Validate original required fields
    formData.forEach((label) => {
      if (label === "Alternative Mobile Number") return;

      if (!formValues[label] || formValues[label].trim() === "") {
        tempErrors[label] = "This field is required";
        isValid = false;
      }
      if (
        label.toLowerCase().includes("email") &&
        formValues[label] &&
        !validateEmail(formValues[label])
      ) {
        tempErrors[label] = "Enter a valid email address";
        isValid = false;
      }
    });

    // Validate Agreement Checkbox
    if (!formValues["I agree to be contacted by the company. *"]) {
      tempErrors["I agree to be contacted by the company. *"] =
        "You must agree to be contacted";
      isValid = false;
    }

    // Validate Preferred Communication Method
    const comm = formValues["Preferred Communication Method"];
    if (!comm || comm.length === 0) {
      tempErrors["Preferred Communication Method"] =
        "Select at least one method";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      try {
        const payload = {
          data: {
            title: formValues["Title *"],
            firstname: formValues["First Name *"],
            lastname: formValues["Last Name *"],
            email: formValues["Email *"],
            mobile: formValues["Mobile Number *"],
            phone: formValues["Alternative Mobile Number"] || null,
            companyname: formValues["Company Name *"],
            consentprovided: formValues["I agree to be contacted by the company. *"],
            preferredcommunicationmethod: joinCommunicationMethods(
              formValues["Preferred Communication Method"] || []
            ),
            origin: "Website",
          },
        };

        // await submitFormAPI(payload);

        setSnackbarMessage("Form submitted successfully!");
        setOpenSnackbar(true);

        // Reset form
        setFormValues({
          "Title *": "",
          "First Name *": "",
          "Last Name *": "",
          "Mobile Number *": "",
          "Alternative Mobile Number": "",
          "Email *": "",
          "Company Name *": "",
          "I agree to be contacted by the company. *": false,
          "Preferred Communication Method": [],
        });
      } catch (error) {
        setSnackbarMessage("Failed to submit form. Please try again.");
        setOpenSnackbar(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <ToastMessages
        isOpen={openSnackbar}
        handleClose={() => setOpenSnackbar(false)}
        data={snackbarMessage}
        severity={snackbarMessage.includes("successfully") ? true : false}
      />

      <Box sx={{ backgroundColor: COLORS.purpleColor, color: "white", py: 5, px: 5 }}>
        <Typography
          variant="h3"
          fontWeight="bold"
          textAlign="center"
          fontFamily={fontFamilyAll}
        >
          Contact Us
        </Typography>

        <Grid container spacing={4} alignItems="center" justifyContent="center" mt={2}>
          {/* LEFT COLUMN: Contact Info + Map */}
          <Grid item xs={12} md={5} mb={12}>
            {data.map((detail, index) => (
              <Typography
                key={index}
                component="a"
                href={detail.link}
                variant="h6"
                fontFamily={fontFamilyAll}
                sx={{
                  mt: 2,
                  display: "flex",
                  alignItems: "center",
                  fontWeight: "bold",
                  textDecoration: "none",
                  color: "inherit",
                  "&:hover": { color: COLORS.pinkColor },
                }}
              >
                {detail.icon} {detail.text}
              </Typography>
            ))}

            <Box sx={{ borderRadius: 3, textAlign: "left", mt: 3 }}>
              <iframe
                title="Google Maps"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3581.473830707999!2d28.041218800000003!3d-26.1486937!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e9573c1da706ef5%3A0x9202607f9dc6f18!2sBig%20Five%20CRM%20Technologies%20(Pty)%20Ltd!5e0!3m2!1sen!2sin!4v1747914566030!5m2!1sen!2sin"
                width="100%"
                height="630"
                style={{ borderRadius: "10px", border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </Box>
          </Grid>

          {/* RIGHT COLUMN: Form */}
          <Grid item xs={12} md={6} mb={9}>
            <Grid container spacing={2}>
              {/* Title, First Name, Last Name */}
              <Grid container item xs={12} spacing={2}>
                {formData.slice(0, 3).map((label, index) => (
                  <Grid
                    item
                    xs={label === "Title *" ? 3 : 4.5}
                    key={index}
                  >
                    <Typography
                      sx={{ color: COLORS.pinkColor, textAlign: "left", fontSize: 14 }}
                      fontFamily={fontFamilyAll}
                    >
                      {label}
                    </Typography>

                    {label === "Title *" ? (
                      <FormControl
                        fullWidth
                        error={Boolean(errors[label])}
                        sx={{ mt: 1 }}
                      >
                        <Select
                          value={formValues[label] || ""}
                          onChange={(e) => handleChange(label, e.target.value)}
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                            color: "white",
                            "& .MuiSelect-icon": { color: "white" },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                color: "black",
                                "& .MuiMenuItem-root": {
                                  "&:hover": {
                                    backgroundColor: "rgba(227, 104, 153, 0.1)",
                                  },
                                  "&.Mui-selected": {
                                    color: "#E36899",
                                    backgroundColor: "rgba(227, 104, 153, 0.2)",
                                  },
                                },
                              },
                            },
                          }}
                        >
                          {titleOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors[label] && (
                          <Typography sx={{ color: "#f44336", fontSize: "12px", mt: 1 }}>
                            {errors[label]}
                          </Typography>
                        )}
                      </FormControl>
                    ) : (
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={formValues[label] || ""}
                        onChange={(e) => handleChange(label, e.target.value)}
                        error={Boolean(errors[label])}
                        helperText={errors[label]}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "white" },
                            "&:hover fieldset": { borderColor: "white" },
                            "&.Mui-focused fieldset": { borderColor: "white" },
                            color: "white",
                            mt: 1,
                          },
                        }}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>

              {/* Remaining Original Fields (Mobile, Email, Company, Message, etc.) */}
              {formData.slice(3).map((label, index) => (
                <Grid item xs={12} key={index + 3}>
                  <Typography
                    sx={{ color: COLORS.pinkColor, textAlign: "left", fontSize: 14 }}
                    fontFamily={fontFamilyAll}
                  >
                    {label}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    multiline={label.includes("Message")}
                    rows={label.includes("Message") ? 3 : 1}
                    value={formValues[label] || ""}
                    onChange={(e) => handleChange(label, e.target.value)}
                    error={Boolean(errors[label])}
                    helperText={errors[label]}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "white" },
                        "&:hover fieldset": { borderColor: "white" },
                        "&.Mui-focused fieldset": { borderColor: "white" },
                        color: "white",
                        mt: 1,
                      },
                    }}
                  />
                </Grid>
              ))}

              {/* NEW: Agreement Checkbox */}

              {/* NEW: Preferred Communication Method (Multi-Select with Chips) */}
              <Grid item xs={12}>
                <Typography
                  sx={{ color: COLORS.pinkColor, textAlign: "left", fontSize: 14 }}
                  fontFamily={fontFamilyAll}
                >
                  Preferred Communication Method *
                </Typography>

                <FormControl
                  fullWidth
                  error={Boolean(errors["Preferred Communication Method"])}
                  sx={{ mt: 1 }}
                >
                  <Select
                    multiple
                    value={formValues["Preferred Communication Method"] || []}
                    onChange={handleMultiSelectChange}
                    input={<OutlinedInput />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            onDelete={() => {
                              const newSelected = (formValues["Preferred Communication Method"] || []).filter(
                                (v) => v !== value
                              );
                              handleChange("Preferred Communication Method", newSelected);
                            }}
                            deleteIcon={<span style={{ color: "white" }}>×</span>}
                            sx={{
                              backgroundColor: COLORS.pinkColor,
                              color: "white",
                              "& .MuiChip-deleteIcon": { color: "white" },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          "& .MuiMenuItem-root": {
                            "&:hover": { backgroundColor: "rgba(227, 104, 153, 0.1)" },
                            "&.Mui-selected": {
                              backgroundColor: "rgba(227, 104, 153, 0.2)",
                              color: COLORS.purpleColor,
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                      color: "white",
                    }}
                  >
                    {communicationOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        <Checkbox
                          checked={
                            (formValues["Preferred Communication Method"] || []).indexOf(opt) > -1
                          }
                          sx={{ color: COLORS.purpleColor, "&.Mui-checked": { color: COLORS.purpleColor } }}
                        />
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors["Preferred Communication Method"] && (
                    <Typography sx={{ color: "#f44336", fontSize: "12px", mt: 0.5 }}>
                      {errors["Preferred Communication Method"]}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(formValues["I agree to be contacted by the company. *"])}
                      onChange={(e) =>
                        handleChange("I agree to be contacted by the company. *", e.target.checked)
                      }
                      sx={{
                        color: "white",
                        "&.Mui-checked": { color: COLORS.pinkColor },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: "white", fontSize: 14, mt: 1.5 }} fontFamily={fontFamilyAll}>
                      I agree to be contacted by the company. *
                    </Typography>
                  }
                  sx={{ alignItems: "flex-start" }}
                />
                {errors["I agree to be contacted by the company. *"] && (
                  <Typography sx={{ color: "#f44336", fontSize: "12px", ml: 4 }}>
                    {errors["I agree to be contacted by the company. *"]}
                  </Typography>
                )}
              </Grid>

              {/* Submit Button */}
              <Grid container justifyContent="center" alignItems="center">
                <Grid item xs={12} md={6} mt={2} ml={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmit}
                    // disabled={isLoading}
                    sx={{
                      backgroundColor: COLORS.pinkColor,
                      color: "white",
                      fontSize: "16px",
                      pointerEvents: isLoading || !formValues["I agree to be contacted by the company. *"] ? "none" : "auto",
                      opacity: isLoading || !formValues["I agree to be contacted by the company. *"] ? 0.5 : 1,
                      fontFamily: fontFamilyAll,
                      borderRadius: 7,
                      textTransform: "capitalize",
                      "&:hover": { backgroundColor: COLORS.pinkColor },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    {isLoading ? (
                      <>
                        <CircularProgress size={20} color="inherit" />
                        Submitting...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}